import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Phone, Mail } from "lucide-react";
import { storeApi, formatStoreError } from "@/lib/storefront";
import { BRAND } from "@/lib/brand";
import { toast } from "sonner";

const SHOP_LINKS = [
  ["All products", "/catalog"],
  ["Fabric", "/c/fabric"],
  ["Quilting Rulers & Templates", "/c/quilting-rulers-templates"],
  ["Cutting Mats & Rotary Cutters", "/c/cutting-mats-rotary-cutters"],
  ["Threads", "/c/threads"],
  ["Notions & Small Tools", "/c/notions-small-tools"],
  ["Patterns & Books", "/c/patterns-books"],
  ["Gift Cards", "/c/gift-cards"],
];

const COMPANY_LINKS = [
  ["About us", "/pages/our-story"],
  ["Wholesale", "/wholesale"],
  ["Line sheet (B2B)", "/wholesale/line-sheet"],
  ["Contact", "/contact"],
];

const HELP_LINKS = [
  ["Shipping", "/pages/delivery"],
  ["Returns & refunds", "/pages/returns"],
  ["FAQ", "/faq"],
  ["Precut size guide", "/guides/precut-sizes"],
  ["Quilt calculators", "/guides/quilt-calculators"],
  ["Quilting glossary", "/guides/glossary"],
];

const LEGAL_LINKS = [
  ["Privacy policy", "/pages/privacy"],
  ["Terms of service", "/pages/terms"],
];

export default function StoreFooter() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setSending(true);
    try {
      const { data } = await storeApi.post("/newsletter", { email });
      toast.success(data.message || "Subscribed");
      setEmail("");
    } catch (err) {
      toast.error(formatStoreError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <footer className="border-t border-[#E6D4CC] bg-[#F1E3DC]" data-testid="store-footer">
      {/* Newsletter */}
      <div className="border-b border-[#E6D4CC] py-16 md:py-20">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <div className="mb-2 text-xs uppercase tracking-[0.3em] text-[#6B5158]">Save 10% on your first order</div>
          <h3 className="font-display text-3xl text-[#2B2127] md:text-5xl">
            Join the <span className="font-display-soft">maker list</span>
          </h3>
          <p className="mt-3 text-sm text-[#6B5158] md:text-base">
            Restock alerts, new fabric drops, and honest shop notes — about one email a month, written by a real person.
          </p>
          <form onSubmit={subscribe} className="mx-auto mt-8 flex max-w-md items-center gap-2 rounded-full border border-[#E6D4CC] bg-white p-1" data-testid="newsletter-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 bg-transparent px-4 py-2 text-sm text-[#2B2127] outline-none placeholder:text-[#6B5158]/50"
              data-testid="newsletter-input"
            />
            <button
              type="submit"
              disabled={sending}
              className="rounded-full bg-[#6D2E4B] px-6 py-2 text-sm text-white transition-colors hover:bg-[#59253d] disabled:opacity-50"
              data-testid="newsletter-submit"
            >
              {sending ? "…" : "Subscribe"}
            </button>
          </form>
          <p className="mt-3 text-[11px] text-[#6B5158]">We only use your email for shop updates. Unsubscribe anytime — see our <Link to="/pages/privacy" className="underline">privacy policy</Link>.</p>
        </div>
      </div>

      {/* Columns */}
      <div className="mx-auto grid max-w-[1440px] gap-10 px-6 py-14 md:grid-cols-5 md:px-10">
        <div>
          <div className="font-display text-2xl text-[#2B2127]">{BRAND.name}</div>
          <p className="mt-3 text-base leading-relaxed text-[#6B5158]">
            {BRAND.tagline}. Fabric, rulers, mats, threads, notions, and patterns — curated for American quilters and sewists, shipped across the US.
          </p>
          <ul className="mt-4 space-y-1.5 text-[14px] text-[#2B2127]">
            <li><a href={BRAND.phoneHref} className="inline-flex items-center gap-2 hover:text-[#9D5C6C]" data-testid="footer-phone"><Phone size={14} className="text-[#9D5C6C]" /> {BRAND.phone}</a></li>
            <li><a href={`mailto:${BRAND.email}`} className="inline-flex items-center gap-2 hover:text-[#9D5C6C]" data-testid="footer-email"><Mail size={14} className="text-[#9D5C6C]" /> {BRAND.email}</a></li>
          </ul>
          <div className="mt-4 flex gap-3 text-[#6B5158]">
            <a href={BRAND.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-[#2B2127]" data-testid="social-instagram"><Instagram size={18} /></a>
            <a href={BRAND.pinterest} target="_blank" rel="noopener noreferrer" aria-label="Pinterest" className="hover:text-[#2B2127]" data-testid="social-pinterest">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <path d="M8.5 20.5c1-2.5 1.5-4.5 2-7M12.3 6.5c-2 0-3.8 1.4-3.8 3.6 0 1.2.5 2 1.3 2.4M12.3 6.5c1.9 0 3.5 1.3 3.5 3.3 0 2.6-1.6 4.7-3.7 4.7-1 0-1.8-.6-1.6-1.6l.6-2.9" />
              </svg>
            </a>
          </div>
        </div>
        <div>
          <div className="mb-4 text-xs uppercase tracking-[0.22em] text-[#6B5158]" data-testid="footer-shop-heading">Shop</div>
          <ul className="space-y-2.5 text-[15px]">
            {SHOP_LINKS.map(([label, href]) => (
              <li key={href}><Link to={href} className="text-[#2B2127] hover:text-[#9D5C6C]">{label}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <div className="mb-4 text-xs uppercase tracking-[0.22em] text-[#6B5158]" data-testid="footer-company-heading">Company</div>
          <ul className="space-y-2.5 text-[15px]">
            {COMPANY_LINKS.map(([label, href]) => (
              <li key={href}><Link to={href} className="text-[#2B2127] hover:text-[#9D5C6C]">{label}</Link></li>
            ))}
            <li><a href={`mailto:${BRAND.wholesaleEmail}`} className="break-all text-[13px] text-[#2B2127] hover:text-[#9D5C6C]">{BRAND.wholesaleEmail}</a></li>
          </ul>
        </div>
        <div>
          <div className="mb-4 text-xs uppercase tracking-[0.22em] text-[#6B5158]" data-testid="footer-help-heading">Help</div>
          <ul className="space-y-2.5 text-[15px]">
            {HELP_LINKS.map(([label, href]) => (
              <li key={label}><Link to={href} className="text-[#2B2127] hover:text-[#9D5C6C]">{label}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <div className="mb-4 text-xs uppercase tracking-[0.22em] text-[#6B5158]" data-testid="footer-legal-heading">Legal</div>
          <ul className="space-y-2.5 text-[15px]">
            {LEGAL_LINKS.map(([label, href]) => (
              <li key={href}><Link to={href} className="text-[#2B2127] hover:text-[#9D5C6C]">{label}</Link></li>
            ))}
          </ul>
          <p className="mt-5 text-[12px] leading-relaxed text-[#6B5158]">
            {BRAND.name} is operated by {BRAND.legalName}.
          </p>
        </div>
      </div>

      <div className="border-t border-[#E6D4CC] py-5">
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-2 px-6 text-sm text-[#6B5158] md:px-10">
          <div>© {new Date().getFullYear()} {BRAND.name} · {BRAND.legalName} · All rights reserved.</div>
          <div className="font-mono text-xs uppercase tracking-[0.2em]">{BRAND.addressLines[0]} · {BRAND.addressLines[1].replace(", USA", "")}</div>
        </div>
      </div>
    </footer>
  );
}
